// src/components/common/bulk/ColumnMappingModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Save, RotateCcw, Loader2, Info, Plus } from 'lucide-react';
import styles from './ColumnMappingModal.module.css';

// Normalise saved mappings: old string format → array, filter empties
const normalizeMappings = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const result = {};
  for (const [key, val] of Object.entries(raw)) {
    if (Array.isArray(val)) {
      const filtered = val.filter(v => v && v.trim());
      if (filtered.length > 0) result[key] = filtered;
    } else if (val && typeof val === 'string' && val.trim()) {
      result[key] = [val.trim()];
    }
  }
  return result;
};

/**
 * Generic column-mapping modal shared by every bulk-import flow (orders, products, ...).
 * The caller supplies the canonical field list and the settings-namespace API functions.
 */
const ColumnMappingModal = ({
  onClose,
  onSaved,
  fields,
  getSettings,
  saveSettings,
  title = 'Column Mappings',
  infoText = 'Map your Excel column headers to the standard fields. Add multiple aliases per '
    + 'field — the first matching column in your file will be used. Leave empty to use the '
    + 'default column name.',
}) => {
  // mappings: { [canonicalKey]: string[] }
  const [mappings, setMappings]         = useState({});
  // draftInputs: current text being typed before adding, keyed by canonicalKey
  const [draftInputs, setDraftInputs]   = useState({});
  const [isLoading, setIsLoading]       = useState(true);
  const [isSaving, setIsSaving]         = useState(false);
  const [error, setError]               = useState(null);
  const [saveSuccess, setSaveSuccess]   = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const settings = await getSettings();
        setMappings(normalizeMappings(settings.columnMappings));
      } catch (err) {
        console.error('Failed to load column mappings:', err);
        setError('Failed to load saved mappings.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [getSettings]);

  const handleDraftChange = (canonicalKey, value) => {
    setDraftInputs(prev => ({ ...prev, [canonicalKey]: value }));
  };

  const handleAddAlias = (canonicalKey) => {
    const val = (draftInputs[canonicalKey] || '').trim();
    if (!val) return;
    setMappings(prev => {
      const existing = prev[canonicalKey] || [];
      if (existing.includes(val)) return prev;
      return { ...prev, [canonicalKey]: [...existing, val] };
    });
    setDraftInputs(prev => ({ ...prev, [canonicalKey]: '' }));
  };

  const handleDraftKeyDown = (e, canonicalKey) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAlias(canonicalKey);
    }
  };

  const handleRemoveAlias = (canonicalKey, idx) => {
    setMappings(prev => {
      const updated = (prev[canonicalKey] || []).filter((_, i) => i !== idx);
      if (updated.length === 0) {
        const next = { ...prev };
        delete next[canonicalKey];
        return next;
      }
      return { ...prev, [canonicalKey]: updated };
    });
  };

  const handleReset = (canonicalKey) => {
    setMappings(prev => {
      const next = { ...prev };
      delete next[canonicalKey];
      return next;
    });
    setDraftInputs(prev => ({ ...prev, [canonicalKey]: '' }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const current = await getSettings();
      await saveSettings({ ...current, columnMappings: mappings });
      setSaveSuccess(true);
      setTimeout(() => {
        onSaved(mappings);
        onClose();
      }, 800);
    } catch (err) {
      setError('Failed to save mappings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearAll = () => {
    setMappings({});
    setDraftInputs({});
  };

  const activeMappingsCount = Object.values(mappings).filter(v => v && v.length > 0).length;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <h3>{title}</h3>
            {activeMappingsCount > 0 && (
              <span className={styles.badge}>{activeMappingsCount} active</span>
            )}
          </div>
          <button onClick={onClose} className={styles.closeBtn} type="button">
            <X size={20} />
          </button>
        </div>

        {/* Info banner */}
        <div className={styles.infoBanner}>
          <Info size={15} />
          <p>{infoText}</p>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {isLoading ? (
            <div className={styles.loading}>
              <Loader2 size={20} className={styles.spin} />
              <span>Loading saved mappings…</span>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Standard Field</th>
                  <th>Default Column Name</th>
                  <th>Your Column Name(s)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {fields.map(field => {
                  const aliases     = mappings[field.key] || [];
                  const isCustomised = aliases.length > 0;
                  const draft        = draftInputs[field.key] || '';
                  return (
                    <tr key={field.key} className={isCustomised ? styles.customisedRow : ''}>
                      <td>
                        <span className={styles.fieldLabel}>{field.label}</span>
                        {field.required && <span className={styles.requiredDot} title="Required field">*</span>}
                      </td>
                      <td>
                        <code className={styles.defaultCode}>{field.key}</code>
                      </td>
                      <td className={styles.aliasesCell}>
                        {aliases.length > 0 && (
                          <div className={styles.chipsList}>
                            {aliases.map((alias, idx) => (
                              <span key={idx} className={styles.chip}>
                                <span className={styles.chipText}>{alias}</span>
                                <button
                                  type="button"
                                  className={styles.chipRemove}
                                  onClick={() => handleRemoveAlias(field.key, idx)}
                                  title="Remove alias"
                                >
                                  <X size={11} />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className={styles.addAliasRow}>
                          <input
                            type="text"
                            value={draft}
                            onChange={e => handleDraftChange(field.key, e.target.value)}
                            onKeyDown={e => handleDraftKeyDown(e, field.key)}
                            placeholder={aliases.length === 0 ? field.key : 'Add another…'}
                            className={`${styles.addAliasInput} ${isCustomised ? styles.inputActive : ''}`}
                          />
                          <button
                            type="button"
                            className={styles.addAliasBtn}
                            onClick={() => handleAddAlias(field.key)}
                            disabled={!draft.trim()}
                            title="Add alias"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </td>
                      <td>
                        {isCustomised && (
                          <button
                            type="button"
                            className={styles.resetBtn}
                            onClick={() => handleReset(field.key)}
                            title="Clear all aliases"
                          >
                            <RotateCcw size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Error */}
        {error && <div className={styles.errorBanner}>{error}</div>}

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClearAll}
            disabled={isSaving || activeMappingsCount === 0}
          >
            Clear All
          </button>
          <div className={styles.footerRight}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              type="button"
              className={`${styles.saveBtn} ${saveSuccess ? styles.saveBtnSuccess : ''}`}
              onClick={handleSave}
              disabled={isSaving || isLoading}
            >
              {isSaving ? (
                <><Loader2 size={15} className={styles.spin} /> Saving…</>
              ) : saveSuccess ? (
                <><Save size={15} /> Saved!</>
              ) : (
                <><Save size={15} /> Save Mappings</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnMappingModal;
